import CalendarPicker from '@crio/crio-react-component/dist/cjs/components/DateTime/CrioCalendarPicker';
import CalendarPickerVariant from '@crio/crio-react-component/dist/cjs/enums/CalendarPickerVariant';
import {
  dateFormat,
  datetimeFormat,
  formatTextField,
  validate,
  formatForDatabase,
} from '@crio/crio-react-component/dist/cjs/components/DateTime/CrioCalendarUtils';
import { useTranslation } from 'react-i18next';
import { RecordQuestion } from '../../../types';
import { AnswerType, QuestionFormatType } from '../../../enums';

/* WARNING
  We definitely do not want to get anything from the context directly at the question-level.
  Context value, when updated, triggers a blind rerender of EVERYTHING that makes use of it
  via useContext. Instead, things like handleAnswerChange and the dataPoints nested inside
  records are provided as props, therefore dodging the expensive context rerenders.
*/
function DateQuestion(props: RecordQuestion) {
  const { t, i18n } = useTranslation();
  const {
    variableName, formatType, recordId, handleAnswerChange, dataPoint, questionId, readOnly,
  } = props;
  const { answer } = dataPoint || {};
  if (dataPoint) {
    dataPoint.answer_type = formatType === QuestionFormatType.DATETIME ? AnswerType.DATE_TIME : AnswerType.DATE;
  }

  const variant = () => {
    switch (formatType) {
      case QuestionFormatType.DATE:
        return CalendarPickerVariant.DATE;
      case QuestionFormatType.DATE_ALLOW_ONGOING:
        return CalendarPickerVariant.DATE_ALLOW_ONGOING;
      case QuestionFormatType.DATETIME:
        return CalendarPickerVariant.DATETIME;
      default:
        return CalendarPickerVariant.DATE;
    }
  };

  return (
    <CalendarPicker
      language={i18n.language || 'en'}
      t={t}
      disabled={readOnly}
      name={variableName || ''}
      variant={variant()}
      selectedDate={`${answer || ''}`}
      popupOkButton
      onDateChange={(value: any) => {
        const dateTime = value && validate(value, variant()) ? value : '';
        const internalFormat = formatType === QuestionFormatType.DATETIME
          ? datetimeFormat
          : dateFormat;

        // Check if it's an unknown date that needs special formatting
        const isUnknownDate = typeof dateTime === 'string'
            && (dateTime.includes('00-') || !validate(dateTime, variant()));

        // eslint-disable-next-line no-nested-ternary
        const formattedAnswer = dateTime === 'ONGOING'
          ? 'ONGOING'
          : isUnknownDate
            ? formatForDatabase(dateTime) // Use formatForDatabase for unknown dates
            : formatTextField(dateTime, internalFormat, i18n.language || 'en'); // Use formatTextField for normal dates

        const newDataPoint = {
          ...dataPoint,
          answer: formattedAnswer,
        };

        handleAnswerChange({
          questionId,
          variableName,
          newDataPoint,
          recordId,
          runRules: newDataPoint?.answer !== dataPoint?.answer,
        });
      }}
    />
  );
}

export default DateQuestion;
